import {
  type DecisionJustificatif,
  formaterJour,
  formaterMontant,
  type PieceEnVue,
} from '@admin-pmvb/core'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { InferResponseType } from 'hono/client'
import { type ReactNode, useState } from 'react'
import { api, ErreurApi, lire } from '../api'
import {
  LIBELLES_ACTION_JUSTIFICATIF,
  LIBELLES_NATURE_OPERATION,
  LIBELLES_SIGNAL_JUSTIFICATIF,
} from '../libelles'
import {
  aideListe,
  aideListeRegistre,
  bandeDeChange,
  confirmationChoisir,
  deviseDuTaux,
  etatChoix,
  gesteDejaDansLePortail,
  intervalleBilan,
  LISTES_REGISTRE,
  type ListeBilan,
  type ListeRegistre,
  listesDuBilan,
  peutChercher,
  peutChoisir,
  tauxEffectif,
  texteChoix,
  texteEcart,
  texteLigneRegistre,
  texteRecherche,
  texteReleve,
  texteSansChoix,
  titreListeRegistre,
  titrePieceRetenue,
} from './bilan-justificatifs'
import { LogoConnu } from './Logo'
import { formaterInstant } from './Historique'
import { Icone, type NomIcone } from './Icone'
import { confirmer } from './confirmation'

// Le bilan des justificatifs d'une connexion (Tiime, Dougs) : sa requête, les décisions d'un
// humain et la recherche dans les mails (hooks partagés avec la page, `BilanEcritures`), les
// listes repliées du bas de la page (pièces sans opération, justificatifs du portail sans
// liaison et, à l'étape d'écriture, documents déposés par l'agent pas encore reliés et ceux à
// vérifier), et les opérations à choisir sur la fiche d'une tâche. Un humain y décide : écarter
// (« Pas de justificatif attendu »), rouvrir, reconnaître une pièce déjà dans le portail ou la
// contester, choisir la pièce d'une opération à trancher (« C'est celle-ci », décision de
// Pierre-Marie du 13/10/2026 : l'agent du Mac la dépose et la relie au prochain passage en
// écriture, avec les garde-fous d'une sûre ; quand le débit du relevé est apparié, le même geste
// fait son rapprochement bancaire) ou annuler ce choix ; il demande aussi une recherche dans les
// mails d'une opération. Chaque opération appariée dit son débit du relevé et où en est son
// rapprochement bancaire. Aucun bouton n'écrit chez le portail.

export type BilanConnexion = InferResponseType<
  (typeof api.portails)[':id']['justificatifs']['$get'],
  200
>
export type OperationBilan = BilanConnexion['operations'][number]
export type PieceResumee = BilanConnexion['piecesSansOperation'][number]
type LigneRegistre = BilanConnexion['aVerifier'][number] & { tentativesLiaison?: number }

/**
 * Le bilan d'une connexion ; relu toutes les cinq secondes pendant la lecture d'un passage ou tant
 * qu'une recherche dans les mails demandée attend son résultat, toutes les trente secondes tant
 * qu'un passage attend son ordinateur, jamais pendant un défi (`intervalleBilan`). Introuvable
 * (hors périmètre, pas un portail de justificatifs) : aucun nouvel essai.
 */
export const requeteBilan = (id: string) =>
  queryOptions({
    queryKey: ['portails', id, 'justificatifs'],
    queryFn: () => lire(api.portails[':id'].justificatifs.$get({ param: { id } })),
    retry: (essais, e) => !(e instanceof ErreurApi && e.statut < 500) && essais < 3,
    refetchInterval: (q) => intervalleBilan(q.state.data),
  })

/** Jour AAAA-MM-JJ à la française ; tel quel s'il est illisible (jamais d'écran blanc). */
function jour(j: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(j) ? formaterJour(j) : j
}

export function useDecisions(connexionId: string) {
  const client = useQueryClient()
  const [enCours, setEnCours] = useState<string | null>(null)
  const [erreur, setErreur] = useState<{ operationId: string; message: string } | null>(null)
  /** Vrai quand la décision est confirmée et enregistrée. */
  async function decider(d: DecisionJustificatif, confirmation?: string): Promise<boolean> {
    if (confirmation && !(await confirmer(confirmation))) return false
    setErreur(null)
    setEnCours(d.operationId)
    try {
      await lire(api.portails[':id'].justificatifs.$post({ param: { id: connexionId }, json: d }))
      // Le plan est recalculé et les tâches recomptées : tout ce qui en dépend est relu.
      await client.invalidateQueries()
      return true
    } catch (e) {
      setErreur({
        operationId: d.operationId,
        message: e instanceof ErreurApi ? e.message : 'Décision impossible.',
      })
      return false
    } finally {
      setEnCours(null)
    }
  }
  return { decider, enCours, erreur }
}

export type Decisions = ReturnType<typeof useDecisions>

/**
 * « Chercher dans les mails » : la demande est posée, le worker la mène au nom du titulaire de la
 * connexion ; le résultat se lit au rafraîchissement du bilan.
 */
export function useRecherches(connexionId: string) {
  const client = useQueryClient()
  const [enCours, setEnCours] = useState<string | null>(null)
  const [erreur, setErreur] = useState<{ operationId: string; message: string } | null>(null)
  async function chercher(operationId: string) {
    setErreur(null)
    setEnCours(operationId)
    try {
      await lire(
        api.portails[':id'].justificatifs.recherche.$post({
          param: { id: connexionId },
          json: { operationId },
        }),
      )
      await client.invalidateQueries({ queryKey: ['portails', connexionId, 'justificatifs'] })
    } catch (e) {
      setErreur({
        operationId,
        message: e instanceof ErreurApi ? e.message : 'Recherche impossible.',
      })
    } finally {
      setEnCours(null)
    }
  }
  return { chercher, enCours, erreur }
}

export type Recherches = ReturnType<typeof useRecherches>

/** Les pièces de l'appli datées de la période, sans opération en vue chez le portail (repliées). */
export function PiecesSansOperation({ b }: { b: BilanConnexion }) {
  return (
    <Repliable
      icone="description"
      titre="Pièces sans opération"
      nombre={b.piecesSansOperation.length}
      aide={`Factures de l’appli datées de la période, sans opération en vue chez ${b.connexion.portail} : payées par un autre compte, pas encore débitées, ou à vérifier.`}
      ouverte={false}
    >
      <ul className="bilan-pieces">
        {b.piecesSansOperation.map((p) => (
          <li key={p.id} className="bilan-piece">
            <ResumePiece id={p.id} piece={p} />
          </li>
        ))}
      </ul>
    </Repliable>
  )
}

const ICONES_REGISTRE: Record<ListeRegistre, NomIcone> = {
  deposes: 'upload',
  a_verifier: 'warning',
}

/**
 * Étape d'écriture : les documents déposés par l'agent du Mac pas encore reliés, puis les dépôts à
 * vérifier chez le portail (interrompus, orphelins, non reliés depuis sept jours), repliés. Aucun
 * bouton : on vérifie chez le portail, un dépôt interrompu se tranche sur la fiche de sa pièce.
 */
export function Registre({ b }: { b: BilanConnexion }) {
  const portail = b.connexion.portail
  const lignes: Record<ListeRegistre, readonly LigneRegistre[]> = {
    deposes: b.deposes,
    a_verifier: b.aVerifier,
  }
  const jours = new Map(b.operations.map((op) => [op.ref, op.date]))
  return LISTES_REGISTRE.map((l) => (
    <Repliable
      key={l}
      icone={ICONES_REGISTRE[l]}
      titre={titreListeRegistre(l)}
      nombre={lignes[l].length}
      aide={aideListeRegistre(l, { portail })}
      ouverte={false}
    >
      <ul className="bilan-pieces">
        {lignes[l].map((x) => (
          <li key={x.id} className="bilan-piece">
            <div className="bilan-piece-texte">
              <ResumePiece id={x.pieceId} piece={b.pieces[x.pieceId]} />
              <span className="discret">
                {texteLigneRegistre(x, {
                  portail,
                  deposeLe: x.deposeLe ? formaterInstant(x.deposeLe) : null,
                  operationLe: x.operationRef ? (jours.get(x.operationRef) ?? null) : null,
                })}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Repliable>
  ))
}

/**
 * Une liste repliable, avec son nombre et ce qu'elle dit. Un nombre nul (null) : la liste n'a pas
 * été lue, ce n'est pas zéro.
 */
export function Repliable({
  icone,
  titre,
  nombre,
  aide,
  ouverte,
  vide = 'Aucune.',
  children,
}: {
  icone: NomIcone
  titre: string
  nombre: number | null
  aide: string
  ouverte: boolean
  vide?: string
  children: ReactNode
}) {
  return (
    <details className="carte bilan-liste" open={ouverte}>
      <summary>
        <Icone nom="chevron_right" className="bilan-chevron" />
        <Icone nom={icone} />
        <span className="bilan-titre">{titre}</span>
        <span className="mono">{nombre ?? 'non lus'}</span>
      </summary>
      <p className="discret bilan-aide">{aide}</p>
      {nombre === null ? null : nombre === 0 ? <p className="discret">{vide}</p> : children}
    </details>
  )
}

type ContexteOperation = {
  b: BilanConnexion
  ecriture: boolean
  decisions: Decisions
  recherches: Recherches
}

/**
 * Les opérations à choisir d'une connexion de justificatifs, en bref, pour la fiche d'une tâche
 * « justificatifs à choisir » : leurs candidates, « C'est celle-ci » sur chacune, « Annuler ce
 * choix », « Pas de justificatif attendu » et « Chercher dans les mails » (`ecriture` : le lecteur
 * écrit sur l'entité ; le serveur décide), au plus `max` (cinq par défaut), puis le lien vers le
 * bilan complet.
 */
export function ATrancherDeLaConnexion({
  connexionId,
  ecriture,
  max = 5,
  aide = true,
}: {
  connexionId: string
  ecriture: boolean
  max?: number
  /** L'aide de la liste ; sans, quand la fiche de la tâche dit déjà le geste. */
  aide?: boolean
}) {
  const bilan = useQuery(requeteBilan(connexionId))
  const decisions = useDecisions(connexionId)
  const recherches = useRecherches(connexionId)
  if (bilan.isPending) return <p className="discret">Chargement des justificatifs à choisir…</p>
  if (!bilan.data) return <p className="alerte">Bilan des justificatifs introuvable.</p>
  const b = bilan.data
  const ops = listesDuBilan(b.operations).a_trancher
  const montrees = ops.slice(0, Math.max(0, max))
  const reste = ops.length - montrees.length
  const contexte: ContexteOperation = { b, ecriture, decisions, recherches }
  return (
    <div className="bilan-a-trancher">
      {aide && (
        <p className="discret">
          {aideListe('a_trancher', {
            portail: b.connexion.portail,
            ecritureOuverte: b.ecriture.active,
            depotPermis: b.passageCalcule?.depotPermis ?? true,
          })}
        </p>
      )}
      {ops.length === 0 ? (
        <p className="discret">Plus rien à choisir au dernier calcul.</p>
      ) : (
        montrees.map((op) => (
          <Operation key={op.id} op={op} liste="a_trancher" contexte={contexte} />
        ))
      )}
      <p className="discret">
        {reste > 0 && `… et ${reste} autre${reste > 1 ? 's' : ''}. `}
        <Link to="/justificatifs/$id" params={{ id: connexionId }}>
          Ouvrir le bilan
        </Link>
      </p>
    </div>
  )
}

function Operation({
  op,
  liste,
  contexte,
}: {
  op: OperationBilan
  liste: ListeBilan
  contexte: ContexteOperation
}) {
  const { b, ecriture, decisions, recherches } = contexte
  const portail = b.connexion.portail
  const occupe = decisions.enCours !== null || recherches.enCours !== null
  const erreur =
    decisions.erreur?.operationId === op.id
      ? decisions.erreur.message
      : recherches.erreur?.operationId === op.id
        ? recherches.erreur.message
        : null
  const piece = (id: string) => b.pieces[id]
  // « C'est celle-ci » : le choix vaut décision, l'agent du Mac relie au prochain passage.
  const choisir = (pieceId: string) =>
    ecriture &&
    peutChoisir(op, { pieceId, depot: piece(pieceId)?.depot }) && (
      <button
        type="button"
        className="bouton-discret"
        disabled={occupe}
        onClick={() =>
          void decisions.decider(
            { operationId: op.id, choix: 'choisir', pieceId },
            confirmationChoisir({
              portail,
              releveARapprocher: op.releve?.aRapprocher ?? false,
            }),
          )
        }
      >
        C’est celle-ci
      </button>
    )
  const choix = texteChoix(
    {
      ...op,
      choix: op.choix && {
        ...op.choix,
        le: op.choix.le ? formaterInstant(op.choix.le) : null,
      },
    },
    { ecritureOuverte: b.ecriture.active, libelleMotif: op.libelleMotif },
  )
  const annulable = ecriture && etatChoix(op) !== null
  const sansChoix = texteSansChoix(op, portail)
  const recherche = texteRecherche(op, {
    rechercheLe: op.rechercheLe ? formaterInstant(op.rechercheLe) : null,
    demandeeLe: op.rechercheDemandeeLe ? formaterInstant(op.rechercheDemandeeLe) : null,
  })
  const cherchable =
    ecriture &&
    (liste === 'a_trancher' || liste === 'a_fournir') &&
    peutChercher(op, b.rechercheMails.motif)
  const reconnaitre = (pieceId: string) =>
    ecriture && (
      <button
        type="button"
        className="bouton-discret"
        disabled={occupe}
        onClick={() =>
          void decisions.decider(
            { operationId: op.id, choix: 'reconnaitre', pieceId },
            `Cette pièce est-elle bien celle déjà dans ${portail} pour cette opération ? Elle y sera marquée « déjà dans ${portail} » et ne sera jamais déposée.`,
          )
        }
      >
        C’est celle-ci, déjà dans {portail}
      </button>
    )
  const contester = (pieceId: string) =>
    ecriture && (
      <button
        type="button"
        className="bouton-discret"
        disabled={occupe}
        onClick={() =>
          void decisions.decider(
            { operationId: op.id, choix: 'contester', pieceId },
            `Ce n’est pas la pièce de cette opération ? Elle ne lui sera plus proposée et ne sera plus marquée « déjà dans ${portail} ».`,
          )
        }
      >
        Ce n’est pas cette pièce
      </button>
    )
  // Écarter : une opération sans justificatif chez le portail, que la règle ne relie pas seule.
  const ecartable =
    ecriture &&
    op.decision !== 'ecartee' &&
    op.justificatifsSource === 0 &&
    (liste === 'a_trancher' || liste === 'a_fournir' || liste === 'mails')

  let corps: ReactNode = null
  switch (liste) {
    case 'relierait': {
      // Choisie par un humain : la pièce choisie seule, parmi les candidates de la règle.
      const retenue = op.choix && op.pieceId === op.choix.pieceId ? op.pieceId : null
      corps = (
        <>
          {op.action && <div className="discret">{LIBELLES_ACTION_JUSTIFICATIF[op.action]}</div>}
          <PiecesEnVue
            op={op}
            vues={
              retenue
                ? [op.candidates.find((c) => c.pieceId === retenue) ?? vueSeule(retenue)]
                : op.candidates
            }
            piece={piece}
          />
          {choix && <div className="discret">{choix}</div>}
        </>
      )
      break
    }
    case 'reliee':
      corps = op.pieceId && (
        <ul className="bilan-pieces">
          <li className="bilan-piece">
            <ResumePiece id={op.pieceId} piece={piece(op.pieceId)} />
          </li>
        </ul>
      )
      break
    case 'a_trancher':
      corps = (
        <>
          <PiecesEnVue op={op} vues={op.candidates} piece={piece} action={choisir} />
          {/* L'aide de la liste dit le geste une fois ; sous l'opération, seulement le choix fait. */}
          {(choix ?? sansChoix ?? !ecriture) && (
            <div className="discret">
              {choix ??
                sansChoix ??
                `À relier à la main dans ${portail}, ou à choisir par une personne qui écrit sur l’entité : le passage suivant la reconnaît.`}
            </div>
          )}
          {recherche && <div className="discret">{recherche}</div>}
        </>
      )
      break
    case 'deja': {
      // Contester seulement une pièce reconnue au registre ; reconnaître seulement une pièce
      // déposable : sinon le serveur refuserait (déjà chez le cabinet par une autre voie).
      const retenue = op.pieceId
      corps = retenue ? (
        <PiecesEnVue
          op={op}
          vues={[op.candidates.find((c) => c.pieceId === retenue) ?? vueSeule(retenue)]}
          piece={piece}
          action={() =>
            gesteDejaDansLePortail({
              retenue: true,
              reconnue: op.reconnue,
              depot: piece(retenue)?.depot,
            }) === 'contester' && contester(retenue)
          }
          titre={titrePieceRetenue(op.reconnue, portail, op.arrivee)}
        />
      ) : (
        op.candidates.length > 0 && (
          <PiecesEnVue
            op={op}
            vues={op.candidates}
            piece={piece}
            action={(id) =>
              gesteDejaDansLePortail({
                retenue: false,
                reconnue: false,
                depot: piece(id)?.depot,
              }) === 'reconnaitre' && reconnaitre(id)
            }
            titre="Pièces de l’appli en vue"
          />
        )
      )
      break
    }
    case 'mails':
      corps = (
        <>
          <ul className="bilan-pieces">
            {op.piecesMails.map((id) => (
              <li key={id} className="bilan-piece">
                <ResumePiece id={id} piece={piece(id)} />
              </li>
            ))}
          </ul>
          {op.rechercheLe && (
            <div className="discret">Retrouvée le {formaterInstant(op.rechercheLe)}.</div>
          )}
        </>
      )
      break
    case 'a_fournir':
      corps = (recherche || choix || (op.rechercheMotif && !op.rechercheLe)) && (
        <>
          {choix && <div className="discret">{choix}</div>}
          {recherche ? (
            <div className="discret">{recherche}</div>
          ) : (
            op.rechercheMotif && <div className="discret">{op.rechercheMotif}</div>
          )}
        </>
      )
      break
    case 'ecartee':
    case 'hors_champ':
      corps = null
      break
  }

  return (
    <div className="ligne bilan-operation">
      <div className="ligne-contenu">
        <div className="bilan-operation-titre">
          <span className="titre-logo">
            <LogoConnu nom={op.marchand ?? op.libelle} taille={22} />
            <strong>{op.marchand ?? op.libelle}</strong>
          </span>
          <span className="montant">{formaterMontant(op.montantCts, op.devise)}</span>
        </div>
        <div className="discret">
          {[
            jour(op.date),
            LIBELLES_NATURE_OPERATION[op.nature],
            op.marchand && op.libelle !== op.marchand ? op.libelle : null,
            op.ventilee ? 'ventilée' : null,
            op.provisoire ? 'pas encore réalisée' : null,
            op.validee ? `validée dans ${portail}` : null,
            op.justificatifsSource > 0
              ? `${op.justificatifsSource} justificatif${op.justificatifsSource > 1 ? 's' : ''} dans ${portail}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {op.libelleMotif && <div className="discret bilan-motif">{op.libelleMotif}</div>}
        {op.releve && <ReleveDuDebit releve={op.releve} />}
        {corps}
      </div>
      {(ecartable || op.decision === 'ecartee' || annulable || cherchable) && ecriture && (
        <div className="ligne-actions">
          {annulable && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() =>
                void decisions.decider(
                  { operationId: op.id, choix: 'rouvrir' },
                  'Annuler ce choix ? L’opération redevient à choisir ; l’agent ne la reliera pas. Un rapprochement bancaire déjà fait reste : il s’annule depuis le relevé.',
                )
              }
            >
              Annuler ce choix
            </button>
          )}
          {cherchable && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() => void recherches.chercher(op.id)}
            >
              Chercher dans les mails
            </button>
          )}
          {ecartable && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() => void decisions.decider({ operationId: op.id, choix: 'ecarter' })}
            >
              Pas de justificatif attendu
            </button>
          )}
          {op.decision === 'ecartee' && (
            <button
              type="button"
              className="bouton-discret"
              disabled={occupe}
              onClick={() => void decisions.decider({ operationId: op.id, choix: 'rouvrir' })}
            >
              Rouvrir
            </button>
          )}
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}

/**
 * Le même débit sur le relevé de la banque (décision de Pierre-Marie du 13/10/2026) : compte, jour
 * et montant, puis le lien vers son rapprochement bancaire s'il reste à faire, ou les paiements
 * prévus reliés.
 */
export function ReleveDuDebit({ releve }: { releve: NonNullable<OperationBilan['releve']> }) {
  const t = texteReleve(releve)
  return (
    <div className="discret bilan-releve">
      <div>{t.debit}</div>
      {t.suite && <div>{t.suite}</div>}
      {t.rapprocher && (
        <div>
          <Link to="/rapprochements/$id" params={{ id: releve.transactionId }}>
            {t.lien}
          </Link>
        </div>
      )}
    </div>
  )
}

/** Une pièce sans ses signaux (reconnue par un humain, absente des candidates de la règle). */
export const vueSeule = (pieceId: string): PieceEnVue => ({
  pieceId,
  score: null,
  signaux: [],
  ecartJours: 0,
  rapportChange: null,
})

/** Les pièces en vue d'une opération : signaux, écart, score et taux effectif de chacune. */
function PiecesEnVue({
  op,
  vues,
  piece,
  action,
  titre,
}: {
  op: OperationBilan
  vues: readonly PieceEnVue[]
  piece: (id: string) => PieceResumee | undefined
  action?: (pieceId: string) => ReactNode
  titre?: string
}) {
  if (vues.length === 0) return null
  return (
    <div className="bilan-vues">
      {titre && <div className="discret">{titre}</div>}
      <ul className="bilan-pieces">
        {vues.map((v) => {
          const p = piece(v.pieceId)
          return (
            <li key={v.pieceId} className="bilan-piece">
              <div className="bilan-piece-texte">
                <ResumePiece id={v.pieceId} piece={p} />
                <span className="discret">{detailsDeLaVue(v, op, p)}</span>
              </div>
              {action?.(v.pieceId)}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Signaux, écart, score ; taux effectif d'une pièce en devise, avec la bande du dollar. */
export function detailsDeLaVue(v: PieceEnVue, op: OperationBilan, p: PieceResumee | undefined) {
  const parties: string[] = []
  if (v.signaux.length > 0)
    parties.push(v.signaux.map((s) => LIBELLES_SIGNAL_JUSTIFICATIF[s]).join(', '))
  if (v.signaux.length > 0 || v.score !== null) parties.push(texteEcart(v.ecartJours))
  if (v.score !== null) parties.push(`score ${v.score}`)
  if (v.rapportChange !== null) {
    const devise = deviseDuTaux(p, op.devise)
    const symbole = op.devise === 'EUR' ? '€' : op.devise
    const bande = devise ? bandeDeChange(devise) : null
    parties.push(
      devise
        ? `taux effectif ${tauxEffectif(v.rapportChange)} ${symbole} pour 1 ${devise}${bande ? ` (bande ${bande})` : ''}`
        : `taux effectif ${tauxEffectif(v.rapportChange)} (devise de la pièce non lue)`,
    )
  }
  return parties.join(' · ')
}

/** Une pièce de l'appli, vers sa fiche : émetteur, numéro, jour, montant. */
export function ResumePiece({ id, piece }: { id: string; piece: PieceResumee | undefined }) {
  return (
    <span className="bilan-piece-resume">
      <LogoConnu nom={piece?.emetteur} taille={18} />{' '}
      <Link to="/pieces/$id" params={{ id }}>
        {piece?.emetteur ?? piece?.nom ?? 'Pièce'}
      </Link>
      {piece && (
        <span className="discret">
          {' '}
          {[
            piece.emetteur && piece.nom ? piece.nom : null,
            piece.numero ? `n° ${piece.numero}` : null,
            jour(piece.jour),
            piece.montantCts !== null
              ? formaterMontant(piece.montantCts, piece.deviseLue ?? piece.devise)
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
    </span>
  )
}

/**
 * Justificatifs déjà chez le portail, reliés à aucune opération : listés à part, avec leur nombre.
 * Ceux de l'exercice examiné (celui qui contient le début de la période décidée, arrivés depuis
 * son premier jour) ; l'historique, à part et replié (décisions de Pierre-Marie du 12/10/2026). Un agent d'avant 0.5.1 les compte tous
 * ensemble : une seule liste, comme avant.
 */
export function SansLiaison({ b }: { b: BilanConnexion }) {
  const d = b.documentsSansLiaison
  const portail = b.connexion.portail
  const exerciceDu = b.passageCalcule?.exerciceDu ?? null
  const historique = d?.anterieurs && exerciceDu ? d.anterieurs : null
  const depuis =
    historique && exerciceDu ? ` depuis le ${jour(exerciceDu)}, début de l’exercice examiné,` : ''
  return (
    <>
      <Repliable
        icone="receipt_long"
        titre={`Justificatifs dans ${portail} sans liaison`}
        nombre={d ? d.nombre : null}
        aide={
          d
            ? `Arrivés dans ${portail}${depuis} et reliés à aucune opération. ${portail} les renomme : l’appli ne les reconnaît pas sûrement, à vérifier dans ${portail}.`
            : `Pas lus au dernier passage : vérifier dans ${portail} qu’aucun justificatif sans liaison ne ferait doublon.`
        }
        ouverte={false}
        vide="Aucun."
      >
        {d && <ExemplesSansLiaison liste={d} portail={portail} />}
      </Repliable>
      {historique && exerciceDu && (
        <Repliable
          icone="history"
          titre="Historique : reliés à rien avant l’exercice"
          nombre={historique.nombre}
          aide={`Arrivés dans ${portail} avant le ${jour(exerciceDu)}, reliés à aucune opération : des exercices précédents.`}
          ouverte={false}
          vide="Aucun."
        >
          <ExemplesSansLiaison liste={historique} portail={portail} />
        </Repliable>
      )}
    </>
  )
}

/** Les exemples d'une liste de justificatifs sans liaison, et combien d'autres. */
function ExemplesSansLiaison({
  liste,
  portail,
}: {
  liste: { nombre: number; exemples: { date: string; nom: string }[] }
  portail: string
}) {
  const reste = liste.nombre - liste.exemples.length
  return (
    <ul className="bilan-pieces">
      {liste.exemples.map((x, i) => (
        <li key={`${x.date}-${i}`} className="bilan-piece">
          <span>
            <span className="discret">{jour(x.date)}</span> {x.nom}
          </span>
        </li>
      ))}
      {reste > 0 && (
        <li className="discret">
          … et {reste} autre{reste > 1 ? 's' : ''} dans {portail}.
        </li>
      )}
    </ul>
  )
}
